/*
 * Pure campaign-list derivation, search, filters, counts, order and URL state.
 * Design: docs/2026-09-25-campaigns-list-and-page.md.
 */
import { z } from "zod";

export type CampaignState =
  | "disabled"
  | "setup"
  | "preview"
  | "copy_disagrees"
  | "copy_unchecked"
  | "workspace_off"
  | "switched_off"
  | "drafting";

/** Every state in the order the default sort puts it: what needs the rep first. */
export const CAMPAIGN_STATES: readonly CampaignState[] = [
  "setup",
  "preview",
  "copy_disagrees",
  "copy_unchecked",
  "switched_off",
  "workspace_off",
  "disabled",
  "drafting",
];

/** States only a rep can move forward. The rest wait on the system or are a choice already made. */
const NEEDS_YOU: ReadonlySet<CampaignState> = new Set(["setup", "preview", "copy_disagrees"]);
const needsYou = (state: CampaignState): boolean => NEEDS_YOU.has(state);

type CalendarMode = "booking_link_only" | "connector" | "manual";
export type GoalKind =
  | "meeting"
  | "event_participation"
  | "commercial_exploration"
  | "contribution_research"
  | "resource_delivery"
  | "phone_call";
type SignOffMode = "first_name" | "none" | "full_signature";
/**
 * Hide booking for non-meeting goals; store connector mode with no bound calendar, preserving
 * retired manual-mode drafting. Exploring collaboration and an unset goal still offer booking.
 */
const NO_BOOKING_GOALS = [
  "event_participation",
  "resource_delivery",
  "phone_call",
  "contribution_research",
] as const satisfies readonly GoalKind[];
/** Whether how a time gets booked matters for a campaign with this goal. */
export const bookingApplies = (goal: GoalKind | null): boolean =>
  goal === null || !NO_BOOKING_GOALS.some((kind) => kind === goal);

/** The settings a campaign may set for itself, as the list names them. */
export type OwnSetting = "goal" | "booking" | "admission";
/** What a campaign's own emails disagreed with, when that is why it waits. */
export type Mismatch = "booking_link" | "meeting_length" | "event_date" | "registration_link";

/** One campaign as the three settings reads describe it, before anything is derived. */
export type CampaignFacts = {
  readonly campaignId: string;
  readonly name: string;
  readonly providerCampaignId: number;
  readonly enabled: boolean;
  readonly discovered: boolean;
  /** The reason its first draft waits, or null. */
  readonly holdReason: string | null;
  readonly setupState: "discovered" | "needs_setup" | "preview" | "live" | "drifted" | null;
  readonly goalKind: GoalKind | null;
  readonly calendarMode: CalendarMode;
  /** The settings it sets itself rather than taking from the workspace's defaults. */
  readonly ownSettings: readonly OwnSetting[];
  /** What its emails disagreed with, when a copy hold names it; empty otherwise. */
  readonly mismatches: readonly Mismatch[];
  /** Which level holds its drafting off, or null while it drafts; absent for a disabled campaign. */
  readonly draftingHeldBy: "workspace" | "campaign" | "lead" | null;
  /**
   * Null where the campaign has no sign-off to show, which is a disabled one; "unknown" while the
   * sign-off read has failed, so the rest of the list still shows.
   */
  readonly signOff: SignOffMode | "unknown" | null;
  readonly replies: { readonly lastAt: string | null; readonly lastSevenDays: number };
  /**
   * When the campaign was created in Email Bison, as Bison's own `created_at` reports it; null until
   * a discovery poll has recorded one. It is never when this app first saw the campaign: a workspace
   * onboarded in one poll would give every campaign the same instant.
   */
  readonly providerCreatedAt: string | null;
};

export type CampaignRow = CampaignFacts & { readonly state: CampaignState };

/**
 * The one thing a campaign is doing, first match wins. A copy hold names the state only while the
 * campaign is not drafting: a hold left behind on a campaign someone switched on would otherwise
 * say "waiting" about a campaign that is writing drafts.
 */
export function campaignState(facts: CampaignFacts): CampaignState {
  if (!facts.enabled) return "disabled";
  if (facts.setupState === "needs_setup") return "setup";
  if (facts.setupState === "preview") return "preview";
  const drafting = facts.draftingHeldBy === null;
  if (facts.holdReason !== null && !drafting)
    return facts.holdReason === "campaign_copy_unchecked" ? "copy_unchecked" : "copy_disagrees";
  if (facts.draftingHeldBy === "workspace") return "workspace_off";
  if (!drafting) return "switched_off";
  return "drafting";
}

export const campaignRows = (facts: readonly CampaignFacts[]): CampaignRow[] =>
  facts.map((campaign) => ({ ...campaign, state: campaignState(campaign) }));

export const CAMPAIGN_SHOWS = ["all", "needs_you", "drafting", "not_drafting"] as const;
export type CampaignShow = (typeof CAMPAIGN_SHOWS)[number];
export const CAMPAIGN_REPLIED = ["week", "month", "quiet"] as const;
export type CampaignReplied = (typeof CAMPAIGN_REPLIED)[number];
export const CAMPAIGN_OWN = ["own", "inherits"] as const;
export type CampaignOwn = (typeof CAMPAIGN_OWN)[number];
export const CAMPAIGN_SORTS = ["attention", "name", "recent", "newest", "oldest"] as const;
export type CampaignSort = (typeof CAMPAIGN_SORTS)[number];
const GOAL_KINDS = [
  "meeting",
  "event_participation",
  "commercial_exploration",
  "contribution_research",
  "resource_delivery",
  "phone_call",
] as const satisfies readonly GoalKind[];
/**
 * The booking modes the list filters by, `manual` among them: no rep may choose it any more, and a
 * campaign still stored that way is exactly one a rep needs to find ("Not set up").
 */
const CALENDAR_MODES = [
  "booking_link_only",
  "connector",
  "manual",
] as const satisfies readonly CalendarMode[];
const STATES = [
  "disabled",
  "setup",
  "preview",
  "copy_disagrees",
  "copy_unchecked",
  "workspace_off",
  "switched_off",
  "drafting",
] as const satisfies readonly CampaignState[];

/** What the list is showing. Each field is one control on the page and one parameter in its URL. */
export type CampaignListView = {
  readonly q: string;
  readonly show: CampaignShow;
  readonly state: CampaignState | null;
  readonly goal: GoalKind | null;
  readonly booking: CalendarMode | null;
  readonly own: CampaignOwn | null;
  readonly replied: CampaignReplied | null;
  readonly sort: CampaignSort;
};

export const DEFAULT_CAMPAIGN_VIEW: CampaignListView = {
  q: "",
  show: "all",
  state: null,
  goal: null,
  booking: null,
  own: null,
  replied: null,
  sort: "attention",
};

/*
 * The URL is an untrusted boundary: a hand-edited or stale link falls back to the default for the
 * parameter it got wrong, and keeps the rest.
 */
export function parseCampaignListView(search: string): CampaignListView {
  const params = new URLSearchParams(search);
  const pick = <T extends string>(name: string, options: readonly [T, ...T[]]): T | null => {
    const parsed = z.enum(options).safeParse(params.get(name));
    return parsed.success ? parsed.data : null;
  };
  return {
    q: (params.get("q") ?? "").slice(0, 200),
    show: pick("show", CAMPAIGN_SHOWS) ?? "all",
    state: pick("state", STATES),
    goal: pick("goal", GOAL_KINDS),
    booking: pick("booking", CALENDAR_MODES),
    own: pick("own", CAMPAIGN_OWN),
    replied: pick("replied", CAMPAIGN_REPLIED),
    sort: pick("sort", CAMPAIGN_SORTS) ?? "attention",
  };
}

/** The query string for a view, with the defaults left out so a plain list has a plain URL. */
export function campaignListSearch(view: CampaignListView): string {
  const params = new URLSearchParams();
  if (view.q.trim() !== "") params.set("q", view.q);
  if (view.show !== "all") params.set("show", view.show);
  if (view.state) params.set("state", view.state);
  if (view.goal) params.set("goal", view.goal);
  if (view.booking) params.set("booking", view.booking);
  if (view.own) params.set("own", view.own);
  if (view.replied) params.set("replied", view.replied);
  if (view.sort !== "attention") params.set("sort", view.sort);
  const text = params.toString();
  return text ? `?${text}` : "";
}

/** How many of the Filters control's choices differ from the default: the number on its button. */
/** How many filters narrow the list: the number on the Filters button. The order is not one. */
export const activeFilterCount = (view: CampaignListView): number =>
  [view.state, view.goal, view.booking, view.own, view.replied].filter((value) => value !== null)
    .length;

/** A view with every filter and the search cleared, keeping the tab and the order. */
export const clearedView = (view: CampaignListView): CampaignListView => ({
  ...DEFAULT_CAMPAIGN_VIEW,
  show: view.show,
  sort: view.sort,
});

/*
 * Case and accents are ignored, and so is punctuation between words, so "q4 fintech v3" finds
 * "Q4 Fintech CFOs - v3 (shorter opener)". Every word typed must appear; order does not matter.
 */
const fold = (text: string): string =>
  text
    .normalize("NFKD")
    .replace(/\p{Mark}/gu, "")
    .toLowerCase();

/**
 * Whether a campaign matches what was typed. A search of digits alone, optionally after `#`, is also
 * a Bison campaign number: `1507` and `#1507` find campaign 1507 whatever it is called.
 */
export function matchesSearch(row: Pick<CampaignRow, "name" | "providerCampaignId">, q: string) {
  const query = fold(q).trim();
  if (query === "") return true;
  const number = /^#?(\d+)$/u.exec(query);
  if (number !== null && String(row.providerCampaignId) === number[1]) return true;
  const haystack = fold(row.name);
  return query.split(/[^\p{L}\p{N}]+/u).every((word) => word === "" || haystack.includes(word));
}

const DAY = 24 * 60 * 60 * 1000;

function matchesReplied(row: CampaignRow, replied: CampaignReplied, now: Date): boolean {
  const lastAt = row.replies.lastAt === null ? null : Date.parse(row.replies.lastAt);
  const withinMonth = lastAt !== null && now.getTime() - lastAt <= 30 * DAY;
  switch (replied) {
    case "week":
      return row.replies.lastSevenDays > 0;
    case "month":
      return withinMonth;
    case "quiet":
      return !withinMonth;
  }
}

function matchesShow(row: CampaignRow, show: CampaignShow): boolean {
  switch (show) {
    case "all":
      return true;
    case "needs_you":
      return needsYou(row.state);
    case "drafting":
      return row.state === "drafting";
    case "not_drafting":
      return row.state !== "drafting";
  }
}

/** Everything a view narrows by except its tab, so each tab's count reads over the same set. */
function matchesFilters(row: CampaignRow, view: CampaignListView, now: Date): boolean {
  if (!matchesSearch(row, view.q)) return false;
  if (view.state !== null && row.state !== view.state) return false;
  if (view.goal !== null && row.goalKind !== view.goal) return false;
  // A campaign whose goal no booking serves has no booking to filter by.
  if (view.booking !== null && (!bookingApplies(row.goalKind) || row.calendarMode !== view.booking))
    return false;
  if (view.own === "own" && row.ownSettings.length === 0) return false;
  if (view.own === "inherits" && row.ownSettings.length > 0) return false;
  if (view.replied !== null && !matchesReplied(row, view.replied, now)) return false;
  return true;
}

const byName = (left: CampaignRow, right: CampaignRow): number =>
  left.name.localeCompare(right.name, "en", { numeric: true, sensitivity: "base" }) ||
  left.campaignId.localeCompare(right.campaignId);

/** A campaign's creation instant, or null when none is recorded or what is recorded is no date. */
function createdAt(row: CampaignRow): number | null {
  if (row.providerCreatedAt === null) return null;
  const at = Date.parse(row.providerCreatedAt);
  return Number.isNaN(at) ? null : at;
}

/**
 * By creation date in the direction given. A campaign with no date goes last either way: it is the
 * one the order can say nothing about, and putting it first under "oldest" would claim it is old.
 */
const byCreated =
  (direction: 1 | -1) =>
  (left: CampaignRow, right: CampaignRow): number => {
    const l = createdAt(left);
    const r = createdAt(right);
    if (l === null || r === null)
      return (l === null ? 1 : 0) - (r === null ? 1 : 0) || byName(left, right);
    return direction * (l - r) || byName(left, right);
  };

function compare(sort: CampaignSort): (left: CampaignRow, right: CampaignRow) => number {
  switch (sort) {
    case "name":
      return byName;
    case "attention":
      return (left, right) =>
        CAMPAIGN_STATES.indexOf(left.state) - CAMPAIGN_STATES.indexOf(right.state) ||
        byName(left, right);
    case "recent":
      return (left, right) => {
        const l = left.replies.lastAt === null ? -1 : Date.parse(left.replies.lastAt);
        const r = right.replies.lastAt === null ? -1 : Date.parse(right.replies.lastAt);
        return r - l || byName(left, right);
      };
    case "newest":
      return byCreated(-1);
    case "oldest":
      return byCreated(1);
  }
}

export type CampaignListResult = {
  /** The rows this view shows, in its order. */
  readonly rows: readonly CampaignRow[];
  /** Each tab's count, over the search and filters but not the tab itself. */
  readonly counts: Readonly<Record<CampaignShow, number>>;
  /** Every campaign in the workspace, whatever the view. */
  readonly total: number;
  /** Whether a search or a filter is narrowing the list, so an empty result can say which. */
  readonly narrowed: boolean;
};

export function campaignList(
  rows: readonly CampaignRow[],
  view: CampaignListView,
  now: Date,
): CampaignListResult {
  const filtered = rows.filter((row) => matchesFilters(row, view, now));
  const counts = {
    all: filtered.length,
    needs_you: filtered.filter((row) => matchesShow(row, "needs_you")).length,
    drafting: filtered.filter((row) => matchesShow(row, "drafting")).length,
    not_drafting: filtered.filter((row) => matchesShow(row, "not_drafting")).length,
  };
  return {
    rows: filtered.filter((row) => matchesShow(row, view.show)).sort(compare(view.sort)),
    counts,
    total: rows.length,
    narrowed: view.q.trim() !== "" || activeFilterCount(view) > 0,
  };
}

/**
 * How long ago, in the fewest words that still sort in the head: "12 min", "3 h", "4 d", "6 wk".
 * Anything past a year is "over a year"; the exact date is on the campaign's page.
 */
export function replyAge(lastAt: string | null, now: Date): string | null {
  if (lastAt === null) return null;
  const minutes = Math.max(0, Math.floor((now.getTime() - Date.parse(lastAt)) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days} d ago`;
  const weeks = Math.floor(days / 7);
  if (days < 365) return `${weeks} wk ago`;
  return "over a year ago";
}

/** How many campaigns one option of one filter would show. */
export type FilterOptionCounts = {
  readonly state: (option: CampaignState) => number;
  readonly goal: (option: GoalKind) => number;
  readonly booking: (option: CalendarMode) => number;
  readonly own: (option: CampaignOwn) => number;
  readonly replied: (option: CampaignReplied) => number;
};

/**
 * How many campaigns each filter option would show, with the search and every other filter as they
 * are, over the whole list rather than one tab: a filter is a finer cut than a tab, and choosing one
 * shows every tab. An option that would show nothing can then say so before it is chosen.
 */
export function filterOptionCounts(
  rows: readonly CampaignRow[],
  view: CampaignListView,
  now: Date,
): FilterOptionCounts {
  const counted = (narrowed: CampaignListView): number =>
    rows.filter((row) => matchesFilters(row, narrowed, now)).length;
  return {
    state: (state) => counted({ ...view, state }),
    goal: (goal) => counted({ ...view, goal }),
    booking: (booking) => counted({ ...view, booking }),
    own: (own) => counted({ ...view, own }),
    replied: (replied) => counted({ ...view, replied }),
  };
}
